import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
  sm: {
    boxSize: tokens.globals["spacing-4"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    padding: tokens.globals["spacing-sm"],
    iconSize: tokens.globals["spacing-3"],
    viewBox: "0 0 12 12",
    path: "M2 6L4.5 8.5L10 3",
  },
  md: {
    boxSize: tokens.globals["spacing-5"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    padding: tokens.globals["spacing-md"],
    iconSize: tokens.globals["spacing-4"],
    viewBox: "0 0 16 16",
    path: "M3 8L6.5 11.5L13 5",
  },
  lg: {
    boxSize: tokens.globals["spacing-6"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    padding: tokens.globals["spacing-md"],
    iconSize: tokens.globals["spacing-5"],
    viewBox: "0 0 20 20",
    path: "M4 10L8.5 14.5L16 6",
  },
};

const defaultFocusVisibleState = {
  outline: `${tokens.globals["spacing-0-5"]} solid ${tokens.colors.brand.blue["500"]}`,
  outlineOffset: tokens.globals["spacing-0-5"],
};

const errorState = {
  outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.ui.destructive}`,
  boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

const variantConfig = {
  primary: {
    background: tokens.colors.ui.primary,
    color: tokens.colors.ui["primary-foreground"],
    hover: { background: tokens.colors.brand["aie-blue"]["600"] },
    active: { background: tokens.colors.brand["aie-blue"]["700"] },
  },
  secondary: {
    background: tokens.colors.brand.blue["100"],
    color: tokens.colors.brand["aie-blue"]["500"],
    hover: { background: tokens.colors.brand.blue["200"] },
    active: { background: tokens.colors.brand.blue["300"] },
  },
  destructive: {
    background: tokens.colors.states.alert["50"],
    color: tokens.colors.ui.destructive,
    hover: { background: tokens.colors.states.alert["100"] },
    active: { background: tokens.colors.states.alert["50"] },
  },
};

const getVariantStyles = (variant) => {
  const config = variantConfig[variant] || variantConfig.primary;
  const { background, color, hover, active } = config;
  return { background, color, hover, active };
};

const StyledHiddenCheckbox = styled.input.attrs({ type: "checkbox" })(() => ({
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
}));

const StyledCheckboxBox = styled.span(
  ({ $size = "sm", $variant = "primary", $checked = false, $indeterminate = false }) => {
    const { boxSize, fontSize, lineHeight, padding, iconSize } = sizes[$size];
    const variantStyles = getVariantStyles($variant);
    const isSelected = $checked || $indeterminate;

    // Outline colour: uses variant background when selected, otherwise ui.border
    const outlineColor = isSelected
      ? variantStyles.background
      : tokens.colors.ui.border;

    return {
      width: boxSize,
      height: boxSize,
      borderRadius: "4px",
      background: isSelected ? variantStyles.background : tokens.colors.ui.background,
      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.05)",
      outline: `${tokens.globals["spacing-px"]} solid ${outlineColor}`,
      outlineOffset: `-${tokens.globals["spacing-px"]}`,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      transition: "all 0.15s ease-in-out",
      fontFamily: tokens.typography.fontFamilies["albert-sans"],
      fontWeight: tokens.typography.fontWeights.bold,
      fontSize,
      lineHeight,
      padding,
      color: isSelected ? variantStyles.color : "transparent",

      "&:hover": {
        ...variantStyles.hover,
        outlineColor: isSelected
          ? variantStyles.background
          : tokens.colors.brand.blue["500"],
      },
      "&:active": variantStyles.active,

      "& svg": {
        width: iconSize,
        height: iconSize,
        display: "block",
        flexShrink: 0,
      },
    };
  }
);

const StyledIndeterminateLine = styled.div(({ $size = "sm" }) => ({
  width: sizes[$size].iconSize,
  height: "1.5px",
  background: "currentColor",
  borderRadius: "1px",
  flexShrink: 0,
}));

const StyledCheckboxWrapper = styled.label(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: tokens.globals["spacing-3"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontSize: tokens.typography.fontSize["lg-14px"],
  fontWeight: tokens.typography.fontWeights.medium,
  color: tokens.colors.ui.foreground,
  cursor: "pointer",

  "&:has(input:disabled)": {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  "input:focus-visible + span": defaultFocusVisibleState,

  'input[data-error="true"] + span': errorState,
}));

/**
 * A checkbox component that exactly matches the Figma UI Kit.
 * Supports 3 sizes, 3 variants, and all interactive states.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.size="sm"] - Size: "sm" (16px), "md" (20px), "lg" (24px)
 * @param {string} [props.variant="primary"] - Variant: "primary", "secondary", "destructive"
 * @param {boolean} [props.checked=false] - Checked state
 * @param {boolean} [props.indeterminate=false] - Indeterminate state
 * @param {boolean} [props.disabled=false] - Disabled state
 * @param {boolean} [props.error=false] - Error state (red outline)
 * @param {string} [props.label] - Optional label
 * @param {function} [props.onChange] - Change handler
 * @param {React.Ref} ref - Forwarded ref
 * @returns {JSX.Element}
 */
const Checkbox = forwardRef(
  (
    {
      size = "sm",
      variant = "primary",
      checked = false,
      indeterminate = false,
      disabled = false,
      error = false,
      label,
      onChange,
      ...rest
    },
    ref
  ) => {
    const internalRef = useRef(null);
    useImperativeHandle(ref, () => internalRef.current);

    useEffect(() => {
      if (internalRef.current) {
        internalRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const { viewBox, path } = sizes[size];

    return (
      <StyledCheckboxWrapper>
        <StyledHiddenCheckbox
          ref={internalRef}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          data-error={error ? "true" : undefined}
          {...rest}
        />

        <StyledCheckboxBox
          $size={size}
          $variant={variant}
          $checked={checked}
          $indeterminate={indeterminate}
        >
          {checked && (
            <svg
              viewBox={viewBox}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d={path}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {!checked && indeterminate && (
            <StyledIndeterminateLine $size={size} />
          )}
        </StyledCheckboxBox>

        {label && <span>{label}</span>}
      </StyledCheckboxWrapper>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;